import { normalizePhone } from './normalize.js';

/**
 * The rules for a call that arrives instead of one we place.
 *
 * WHY THIS IS A SEPARATE FILE AND NOT A FLAG ON THE DIALER
 * An inbound call inverts almost every assumption the outbound path is built
 * on, and sharing code between them would mean one of the two sets of rules
 * quietly applying to the other:
 *
 *   OUTBOUND                              INBOUND
 *   we choose who is called               they choose
 *   the gate decides whether we may       there is nothing to permit; they rang us
 *   the called party's time zone rules    the agency's own opening hours rule
 *   we know exactly who we dialled        we know a number, and numbers lie
 *   consent is the question               what they ASK for is the question
 *
 * The last two are the ones this file exists for.
 *
 * ------------------------------------------------------------------------
 * THE ONE THING AN INBOUND LINE MUST NEVER FAIL AT
 * ------------------------------------------------------------------------
 * Somebody rings the number on a card we left and says "take me off your
 * list." 47 CFR 64.1200(d)(3) says that request must be honoured, and it does
 * not care which channel it arrived on. An agency that dials three hundred
 * thousand people and publishes a number that cannot record a removal request
 * is in a worse position than one with no inbound line at all: the call
 * happened, the carrier has the record of it, and there is nothing on our side
 * showing what was asked or that anybody acted on it.
 *
 * So the removal path is the shortest path in this whole subsystem — one
 * action, no form, no disposition first — and it writes to the SAME internal
 * do-not-call list the outbound gate reads. Two lists would mean a number
 * removed at the desk still being dialled tomorrow morning.
 *
 * ------------------------------------------------------------------------
 * A CALLER ID IS A SUGGESTION, NEVER AN IDENTIFICATION
 * ------------------------------------------------------------------------
 * Caller ID is trivially spoofed, households share a handset, and a number
 * that belonged to one person last year belongs to somebody else now. A CRM
 * that opens a household's record — their income, their dependants, their
 * medications — because a matching number appeared on a screen has handed a
 * stranger everything they need, and done it before anybody said hello.
 *
 * `matchCaller` therefore returns a SUGGESTION with its own confidence, and
 * nothing here ever returns "this is who it is". The screen's job is to say
 * "this number is on file for a Maria Gonzalez" and let a human confirm it out
 * loud. `revealable()` below is the rule for what may be shown before they do.
 */

/* ------------------------------------------------------------- matching -- */

export type MatchKind =
  /** No lead in this agency carries this number. */
  | 'none'
  /** Exactly one lead carries it. Still a suggestion. */
  | 'one'
  /** Several do — a household, a shared line, or a duplicate nobody merged. */
  | 'many'
  /** The caller withheld their number, so there is nothing to match on. */
  | 'withheld';

export interface CallerMatch {
  kind: MatchKind;
  /** Lead ids carrying this number, most recently touched first. Never one "winner". */
  leadIds: readonly string[];
  /**
   * What the screen is allowed to say before a human has confirmed who is on
   * the line. Deliberately a sentence rather than a flag: it is read aloud by
   * the person answering, and it has to be true.
   */
  because: string;
}

export interface MatchableLead {
  id: string;
  phoneKey: string | null;
  /** Used only for ordering, so the likeliest suggestion is offered first. */
  lastTouchedAt: Date | null;
}

/**
 * Which leads carry the number this call came from.
 *
 * NO WINNER IS PICKED WHEN THERE ARE SEVERAL, and that is the point. One
 * telephone in a house is answered by whoever is nearest, so a shared number
 * matching a husband and a wife is not a tie to be broken by recency — it is
 * two people and a question for the agent to ask. Picking the more recent one
 * would be right slightly more than half the time, and wrong silently the
 * rest, with the household's own record open on the screen.
 */
export function matchCaller(
  leads: readonly MatchableLead[], fromE164OrDigits: string | null,
): CallerMatch {
  const key = normalizePhone(fromE164OrDigits);
  if (!key) {
    return {
      kind: 'withheld',
      leadIds: [],
      because: 'The caller’s number was withheld or unreadable, so there is nothing to match on.',
    };
  }
  const hits = leads
    .filter((l) => l.phoneKey !== null && l.phoneKey === key)
    .sort((a, b) => (b.lastTouchedAt?.getTime() ?? 0) - (a.lastTouchedAt?.getTime() ?? 0));

  if (hits.length === 0) {
    return { kind: 'none', leadIds: [], because: 'This number is not on any record in this agency.' };
  }
  if (hits.length === 1) {
    return {
      kind: 'one',
      leadIds: [hits[0]!.id],
      because: 'This number is on file for one record. Confirm who you are speaking to before discussing it.',
    };
  }
  return {
    kind: 'many',
    leadIds: hits.map((l) => l.id),
    because: `This number is on file for ${hits.length} records — ask who is calling before opening either.`,
  };
}

/**
 * What may be shown about a matched record before a human has confirmed who is
 * speaking.
 *
 * The answer is: enough to have the conversation, and nothing that would harm
 * anybody if the caller turned out to be somebody else. A first name and a town
 * let an agent say "am I speaking to Maria?" — which is the question that
 * establishes identity. A date of birth, an income, a coverage amount or a
 * medical answer does not help ask that question and is exactly what somebody
 * ringing in with a stolen number would be fishing for.
 *
 * This is not a permission check. It is a rule about what a screen puts in
 * front of somebody's eyes before the thing that makes it safe has happened,
 * and it is expressed as an allow-list so that a field added to `Lead` next
 * month is hidden by default rather than shown by default.
 */
export const REVEALABLE_BEFORE_CONFIRMATION: readonly string[] = [
  'id', 'firstName', 'lastName', 'city', 'state', 'status', 'ownerId',
  'suppressed', 'suppressedReason', 'retiredAt', 'lastContactedAt',
];

export function revealable<T extends Record<string, unknown>>(lead: T): Partial<T> {
  const out: Partial<T> = {};
  for (const k of REVEALABLE_BEFORE_CONFIRMATION) {
    if (k in lead) (out as Record<string, unknown>)[k] = lead[k];
  }
  return out;
}

/* ---------------------------------------------------------------- hours -- */

export interface DeskHours {
  /** IANA zone the agency keeps its own hours in. */
  tz: string;
  /** Local hour the desk opens and closes, 0–24. Close may be before open only if equal. */
  open: number;
  close: number;
  /** Days the desk is staffed, 0 = Sunday. Empty means never. */
  days: readonly number[];
}

/**
 * Nine to six, Monday to Friday, Detroit — because that is where this agency is
 * and a default nobody changed should be the one that is right for them.
 *
 * NOTE THE INVERSION FROM OUTBOUND, because it is the mistake waiting to be
 * made here. An outbound calling window is measured where the CALLED PARTY
 * lives: it is their evening the law protects. Desk hours are measured where
 * the AGENCY is, because they describe whether anybody is sitting at a
 * telephone. Reusing `callingWindow()` for this would give an agency in Detroit
 * different opening hours depending on who happened to ring them.
 */
export const DEFAULT_DESK_HOURS: DeskHours = {
  tz: 'America/Detroit',
  open: 9,
  close: 18,
  days: [1, 2, 3, 4, 5],
};

/** Local hour, with minutes as a fraction, in an arbitrary IANA zone. */
export function hourIn(tz: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(at);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
  const minute = Number(parts.find((p) => p.type === 'minute')?.value ?? '0');
  return (hour % 24) + minute / 60;
}

/** Day of the week, 0 = Sunday, in an arbitrary IANA zone. */
export function dayIn(tz: string, at: Date): number {
  const name = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short' }).format(at);
  return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].indexOf(name);
}

export function deskOpen(at: Date, hours: DeskHours = DEFAULT_DESK_HOURS): boolean {
  if (hours.days.length === 0) return false;
  if (!hours.days.includes(dayIn(hours.tz, at))) return false;
  const h = hourIn(hours.tz, at);
  return h >= hours.open && h < hours.close;
}

/* ----------------------------------------------------------------- ring -- */

export interface RingCandidate {
  userId: string;
  /** They said they are at their desk. */
  available: boolean;
  /** And the platform has seen them recently enough to believe it. */
  fresh: boolean;
  /** A telephone to ring. Somebody with none cannot take a call, whatever else is true. */
  reachable: boolean;
  /** They own the record this number matched, if it matched exactly one. */
  ownsMatch: boolean;
  /**
   * The number that was dialled is pointed at a team they are on.
   *
   * Weaker than the number naming them personally and stronger than owning a
   * record: a team that runs a campaign has a claim on its calls, and it is the
   * claim that scales — an agency does not point forty adverts at forty named
   * people, it points them at the teams that run them.
   */
  onRoutedTeam: boolean;
  /**
   * The number that was dialled is pointed at them.
   *
   * THE STRONGEST CLAIM THERE IS ON AN INBOUND CALL, and it outranks owning
   * the matched record. Somebody ran an advertisement, paid for it, and put a
   * number on it; a call to that number is theirs before it is anybody's.
   * Owning a record in the CRM is a weaker claim — it can be an import that
   * assigned itself, or a name somebody worked once in March.
   */
  ownsNumber: boolean;
}

export type RingPlan =
  | { ring: readonly string[]; voicemail: false; because: string }
  | { ring: readonly []; voicemail: true; because: string };

/**
 * Who this call rings, in order, or why it goes to voicemail instead.
 *
 * THE ORDER IS THE WHOLE DESIGN, and it is one rule: put the call in front of
 * somebody who already knows this household, and if nobody does, in front of
 * anybody at all.
 *
 * A caller who matched one record gets that record's owner first. They have
 * spoken to this person before, the notes are theirs, and the alternative —
 * a colleague opening a stranger's file while the stranger is mid-sentence —
 * is how a returning customer is asked the same four questions twice.
 *
 * WHAT IS DELIBERATELY NOT HERE
 * No licence check. Outbound refuses an agent not licensed in the lead's state,
 * and that is right: WE chose to call that person, so we chose to need the
 * licence. Somebody ringing in has not been solicited, and the agency answering
 * its own telephone is not transacting insurance. Refusing to pick up because
 * nobody on the desk holds a Florida licence would leave a Florida customer
 * listening to a recording, which serves nobody and is not what the licence is
 * for. The agent takes the call, and the moment it turns into business they
 * hand it to somebody licensed — which is `transferTargets`, and already
 * exists.
 *
 * Voicemail is never a failure state here. It is the correct answer at ten
 * past six, and a ring group that hunts for eleven seconds through an empty
 * office before giving up sounds exactly like being ignored.
 */
export function chooseRing(
  candidates: readonly RingCandidate[], open: boolean,
): RingPlan {
  if (!open) {
    return { ring: [], voicemail: true, because: 'The desk is closed.' };
  }
  const able = candidates.filter((c) => c.available && c.fresh && c.reachable);
  if (able.length === 0) {
    /*
     * Said with more precision than "nobody is available", because the three
     * causes want different things done about them and an agency reading its
     * missed calls needs to know which one it is looking at. Somebody who set
     * themselves available and has no number on their record is a record to
     * fix; an empty desk at two in the afternoon is a staffing question.
     */
    const promised = candidates.filter((c) => c.available);
    if (promised.length === 0) {
      return { ring: [], voicemail: true, because: 'Nobody is at the desk.' };
    }
    const stale = promised.filter((c) => !c.fresh);
    if (stale.length === promised.length) {
      return {
        ring: [], voicemail: true,
        because: 'Everybody marked available has gone quiet — the desk is set, but nobody is there.',
      };
    }
    return {
      ring: [], voicemail: true,
      because: 'Everybody at the desk is marked available with no telephone number on their record.',
    };
  }
  /*
   * THE ORDER, MOST SPECIFIC CLAIM FIRST.
   *
   *   1. the number was pointed at them   — they paid for the advertisement
   *   2. the number belongs to their team — their campaign, their calls
   *   3. they own the matched record      — they have spoken to this household
   *   4. everybody else, stably ordered
   *
   * The first beats the second deliberately. An advertisement is a bill
   * somebody paid this month; owning a record is often an import that assigned
   * itself. When a producer buys ads and an agency answers the calls, the
   * commission argument afterwards has no honest resolution — so the routing
   * settles it before the telephone rings rather than after.
   */
  const ordered = [...able].sort((a, b) => {
    if (a.ownsNumber !== b.ownsNumber) return a.ownsNumber ? -1 : 1;
    if (a.onRoutedTeam !== b.onRoutedTeam) return a.onRoutedTeam ? -1 : 1;
    if (a.ownsMatch !== b.ownsMatch) return a.ownsMatch ? -1 : 1;
    return a.userId < b.userId ? -1 : 1;
  });
  const first = ordered[0]!;
  return {
    ring: ordered.map((c) => c.userId),
    voicemail: false,
    because: first.ownsNumber
      ? 'Ringing whoever this number is pointed at first.'
      : first.onRoutedTeam
        ? `Ringing the team this number belongs to \u2014 ${
          ordered.filter((c) => c.onRoutedTeam).length} of them at their desk.`
        : first.ownsMatch
          ? 'Ringing the agent who owns this record first.'
          : `Ringing ${ordered.length} at the desk.`,
  };
}


/* ----------------------------------------------------------- the floor -- */

/**
 * Who sees which calls on the desk screen.
 *
 * ------------------------------------------------------------------------
 * WHY THIS IS NOT "EVERYBODY ON THE ROTA SEES EVERYTHING"
 * ------------------------------------------------------------------------
 * That is what it was, and it is fine for six people in one room. It stops
 * being fine at the size this has to work at. Jake described the shape he
 * actually wants, and it is the shape every agency of any size already has:
 *
 *   a call arrives and says who is ringing;
 *   it rolls up to the owners, who can see how many people are on the
 *     telephones right now;
 *   it goes down to the team leaders, and a leader sees THEIR team;
 *   and under a leader, the agents who are working.
 *
 * So the screen narrows as you go down and widens as you go up, and it is the
 * same narrowing the leads list already uses — `leadScope` in rbac.ts. This is
 * its inbound twin and deliberately mirrors it, because two visibility rules
 * that are almost the same is how one of them ends up showing an agent another
 * team's customers.
 *
 * ------------------------------------------------------------------------
 * THE ONE PLACE IT WIDENS INSTEAD: AN UNCLAIMED CALL
 * ------------------------------------------------------------------------
 * A call that was offered to no team, rang nobody in particular and was
 * answered by nobody — the general line at two in the morning taking a message
 * — belongs to the whole rota, and every rota member sees it.
 *
 * Without that clause the agency's main number would take a message that only
 * an owner could ever find, which is the opposite of a desk. It is also the
 * same fallback `chooseRing` already has: a number pointed at nobody rings
 * everybody, so a message left on one should reach everybody too.
 */
export type DeskScope =
  /** The whole floor. Owner, administrator, compliance. */
  | { kind: 'floor' }
  /** Every team this person leads, and themselves. */
  | { kind: 'teams'; teamIds: readonly string[]; userId: string }
  /** Their own calls, plus anything nobody has claimed. */
  | { kind: 'own'; userId: string };

export interface DeskScopeInput {
  role: string;
  userId: string;
  /** Teams where this user is the leader. */
  ledTeamIds: readonly string[];
}

/**
 * Where this person sits.
 *
 * Leading a team beats holding the `manager` role, exactly as `leadScope` has
 * it: the thing that gives somebody a wider view is having people under them,
 * not a word on their account.
 */
export function deskScope(input: DeskScopeInput): DeskScope {
  if (input.role === 'owner' || input.role === 'admin' || input.role === 'compliance') {
    return { kind: 'floor' };
  }
  if (input.ledTeamIds.length > 0) {
    return { kind: 'teams', teamIds: [...input.ledTeamIds], userId: input.userId };
  }
  return { kind: 'own', userId: input.userId };
}

/** The four fields a call is placed by. Nothing else is read. */
export interface ScopedCall {
  /** The team it was offered to, stamped at the time of routing. */
  offeredTeamId: string | null;
  /** The person it was offered to — the number carried their name. */
  offeredUserId: string | null;
  /** Whose handset is ringing right now. A position in a rotation, not a claim. */
  ringingUserId: string | null;
  /** Who picked it up. */
  answeredBy: string | null;
}

/**
 * A call nobody has a claim on — the house line. See the note on `DeskScope`.
 *
 * `ringingUserId` IS DELIBERATELY NOT A CLAIM, and getting that wrong is what
 * this function is for. The hunt moves that field to the next handset every
 * twenty seconds, so treating it as ownership would mean a call on the
 * agency's general number being visible to exactly one agent at a time, moving
 * from screen to screen as it rang — and invisible to the rest of a desk
 * whose entire job is to answer it.
 *
 * The two real claims are stamped once at routing and never move: the number
 * carried somebody's name, or it carried a team's.
 */
export function unclaimed(call: ScopedCall): boolean {
  return call.offeredTeamId === null
    && call.offeredUserId === null
    && call.answeredBy === null;
}

/**
 * May this person see this call?
 *
 * `teamMemberIds` is everybody on every team the viewer leads, resolved once by
 * the caller rather than per call — a leader with four teams looking at a busy
 * board would otherwise be one membership query per row.
 *
 * THE VIEWER IS ALWAYS IN THEIR OWN SCOPE. An agent whose handset is ringing
 * sees that call whatever team stamped it, because the alternative is a
 * telephone ringing with nothing on the screen to say why.
 */
export function visibleOnDesk(
  scope: DeskScope, call: ScopedCall, teamMemberIds: ReadonlySet<string>,
): boolean {
  if (scope.kind === 'floor') return true;
  if (unclaimed(call)) return true;
  if (call.offeredUserId === scope.userId
    || call.ringingUserId === scope.userId
    || call.answeredBy === scope.userId) return true;
  if (scope.kind === 'own') return false;
  if (call.offeredTeamId !== null && scope.teamIds.includes(call.offeredTeamId)) return true;
  if (call.offeredUserId !== null && teamMemberIds.has(call.offeredUserId)) return true;
  if (call.ringingUserId !== null && teamMemberIds.has(call.ringingUserId)) return true;
  if (call.answeredBy !== null && teamMemberIds.has(call.answeredBy)) return true;
  return false;
}

/* --------------------------------------------------------------- roll-up -- */

/**
 * "You have five active dialers."
 *
 * The line Jake used, and the figure an owner actually looks at. It is not a
 * count of accounts or of people who have logged in this week: it is how many
 * people could pick up a telephone in the next ten seconds — on the rota,
 * marked available, seen recently, with a number to ring. Anything looser
 * reports a staffed desk to somebody whose customers are hearing voicemail.
 */
export interface DeskPerson {
  userId: string;
  /** Granted inbound. Somebody off the rota is not on the desk at any hour. */
  onRota: boolean;
  available: boolean;
  fresh: boolean;
  reachable: boolean;
}

export interface TeamRollUp {
  /** Null is the row for everybody not on a routed team — the house line. */
  teamId: string | null;
  name: string;
  /** Could take a call right now. */
  onDesk: number;
  /** Granted inbound at all, whether or not they are at a desk this minute. */
  onRota: number;
  /** Live calls stamped to this team. */
  live: number;
  /** Messages on this team's calls nobody has listened to. */
  unheard: number;
}

export interface RollUpTeam {
  teamId: string;
  name: string;
  memberIds: readonly string[];
}

/**
 * The per-team summary above the board.
 *
 * ONE PERSON CAN BE ON TWO TEAMS and is counted on both, because the question
 * each row answers is "can this team reach somebody", and the answer is yes for
 * both teams even though there is one human being. Summing the rows therefore
 * over-counts the floor, so the floor total is computed separately from the
 * people rather than by adding the rows up — see `houseRow` below, and the
 * `floor` figure the desk returns.
 */
export function rollUp(
  teams: readonly RollUpTeam[],
  people: readonly DeskPerson[],
  calls: readonly (ScopedCall & { live: boolean; unheardVoicemail: boolean })[],
  opts: { house?: boolean; houseName?: string } = {},
): TeamRollUp[] {
  const houseName = opts.houseName ?? 'The house line';
  const byId = new Map(people.map((p) => [p.userId, p]));
  const able = (p: DeskPerson): boolean => p.onRota && p.available && p.fresh && p.reachable;

  const rows: TeamRollUp[] = teams.map((t) => {
    const members = t.memberIds.map((idOf) => byId.get(idOf)).filter((p): p is DeskPerson => !!p);
    const mine = calls.filter((c) => c.offeredTeamId === t.teamId);
    return {
      teamId: t.teamId,
      name: t.name,
      onDesk: members.filter(able).length,
      onRota: members.filter((p) => p.onRota).length,
      live: mine.filter((c) => c.live).length,
      unheard: mine.filter((c) => c.unheardVoicemail).length,
    };
  });

  /*
   * The house row: everybody on the rota who is on none of these teams, and
   * every call stamped to no team. An agency that has not built teams yet sees
   * exactly one row, which is the right screen for it.
   */
  /*
   * A LEADER GETS NO HOUSE ROW, and that is not cosmetic. The house row counts
   * everybody who is on none of the teams in the list; for an owner that is the
   * rest of the floor, which is what they are entitled to. For a leader passed
   * only their own teams it would be every colleague on every OTHER team,
   * summed into one figure they were never meant to have.
   */
  if (opts.house === false) return rows;

  const onSomeTeam = new Set<string>(teams.flatMap((t) => [...t.memberIds]));
  const loose = people.filter((p) => !onSomeTeam.has(p.userId));
  const houseCalls = calls.filter((c) => c.offeredTeamId === null);
  rows.push({
    teamId: null,
    name: houseName,
    onDesk: loose.filter(able).length,
    onRota: loose.filter((p) => p.onRota).length,
    live: houseCalls.filter((c) => c.live).length,
    unheard: houseCalls.filter((c) => c.unheardVoicemail).length,
  });

  return rows;
}

/* ------------------------------------------------------------- greeting -- */

/**
 * The sentence that makes recording a voicemail lawful, and which no agency may
 * edit away.
 *
 * Eleven states require every party to consent before a call is recorded, and
 * in several of them recording without it is a CRIMINAL statute rather than a
 * civil one — Florida's §934.03 is a third-degree felony. A voicemail is a
 * recording. What makes it lawful is that the caller hears it is being recorded
 * and chooses to speak anyway, which is consent of exactly the kind those
 * statutes ask for.
 *
 * So the notice is appended by the platform, after whatever the agency wrote,
 * and `composeGreeting` is the only way a greeting is built. An agency that
 * could delete this line would be one keystroke from a felony in eight states,
 * and would have no idea.
 */
export const RECORDING_NOTICE =
  'Please leave a message after the tone. Your message will be recorded.';

/** Beyond this, a caller hangs up before the tone. */
export const MAX_GREETING_CHARS = 400;

export interface Greeting {
  text: string;
  /** The agency's own words, after trimming and truncation. */
  custom: string;
  /** True when what the agency wrote had to be shortened. */
  truncated: boolean;
}

export function composeGreeting(agencyName: string, custom?: string | null): Greeting {
  const name = agencyName.trim() || 'this agency';
  const written = (custom ?? '').replace(/\s+/g, ' ').trim();
  const fallback = `Thank you for calling ${name}. There is nobody available to take your call.`;
  const body = written || fallback;
  const truncated = body.length > MAX_GREETING_CHARS;
  const kept = truncated ? `${body.slice(0, MAX_GREETING_CHARS - 1).trimEnd()}…` : body;
  return { text: `${kept} ${RECORDING_NOTICE}`, custom: kept, truncated };
}

/* ---------------------------------------------------------------- state -- */

export const INBOUND_STATES = [
  /** The provider has told us about it; we have not answered yet. */
  'ringing',
  /** Answered by the platform, being offered to the desk. */
  'offering',
  /** An agent picked up and is on the line. */
  'connected',
  /** Nobody took it; the caller is recording a message. */
  'voicemail',
  /** Over, one way or another. */
  'completed',
  /** The caller hung up before anybody or anything reached them. */
  'abandoned',
  /** The provider could not complete it. */
  'failed',
] as const;
export type InboundState = (typeof INBOUND_STATES)[number];

const NEXT: Record<InboundState, readonly InboundState[]> = {
  ringing: ['offering', 'voicemail', 'abandoned', 'failed'],
  /*
   * `offering` → `completed` AND `abandoned` were both missing, and that left
   * calls stranded forever.
   *
   * Once the platform answers, a caller who gives up while handsets are being
   * hunted produces NORMAL_CLEARING — which is simply what an ANSWERED leg
   * clearing looks like — and that maps to `completed`. With the move illegal,
   * `applyProviderEvent` refused it and the row sat in `offering` for good:
   * permanently on the live board (which is capped at fifty, so fifty
   * abandoned calls push every real one off the screen), never written to the
   * lead's timeline, and with no `endedAt`. Nothing sweeps them.
   *
   * `completed` is the honest name for it — we picked up, the conversation
   * with a human never happened, and the caller rang off. `wasAnswered` is
   * what separates that from somebody actually speaking to an agent, and it
   * counts `completed` as answered, which is correct: the AGENCY answered.
   */
  offering: ['connected', 'voicemail', 'completed', 'abandoned', 'failed'],
  /*
   * `connected` → `abandoned` for the same reason in the rarer direction: a
   * cause that means "gave up" arriving on a bridged call was ignored, and the
   * call stuck in `connected`.
   */
  connected: ['completed', 'abandoned', 'failed'],
  voicemail: ['completed', 'abandoned', 'failed'],
  completed: [],
  abandoned: [],
  failed: [],
};

export function canInboundTransition(from: InboundState, to: InboundState): boolean {
  return NEXT[from].includes(to);
}

export function isInboundTerminal(s: InboundState): boolean {
  return NEXT[s].length === 0;
}

/**
 * Whether this call ever reached a person on our side.
 *
 * The one number an agency actually wants from an inbound line, and the reason
 * it is computed here rather than counted on a screen: `voicemail` is NOT
 * answered. A dashboard that counted it as answered would report a perfect
 * pick-up rate for an office where the telephone rings out every afternoon,
 * which is the flattering direction and therefore the one to rule out.
 */
export function wasAnswered(s: InboundState): boolean {
  return s === 'connected' || s === 'completed';
}

/* ------------------------------------------------------------ what next -- */

export type InboundOutcome =
  | 'spoke'
  | 'left_message'
  | 'asked_to_stop'
  | 'wrong_number'
  | 'not_for_us'
  | 'abandoned';

export const INBOUND_OUTCOME_LABEL: Record<InboundOutcome, string> = {
  spoke: 'Spoke with them',
  left_message: 'Left a voicemail',
  asked_to_stop: 'Asked not to be called again',
  wrong_number: 'Wrong number',
  not_for_us: 'Not for us',
  abandoned: 'Hung up before anybody answered',
};

/**
 * The outcomes that must write to the internal do-not-call list.
 *
 * ONE ENTRY TODAY, AND IT IS A LIST ANYWAY. `asked_to_stop` is the whole reason
 * the inbound line carries legal weight, and the day somebody adds a second
 * outcome that means the same thing — "removed at their request", "opted out" —
 * this is the place that decides, rather than a second `if` somewhere that
 * agrees with this one until it does not. The outbound path learned that the
 * hard way: `do_not_contact` and `asked_to_stop` were two names for one event
 * and had to be made to do the same thing after the fact.
 */
export const INBOUND_SUPPRESSING_OUTCOMES: readonly InboundOutcome[] = ['asked_to_stop'];

export function suppressesContact(o: InboundOutcome): boolean {
  return INBOUND_SUPPRESSING_OUTCOMES.includes(o);
}
